import { useCallback, useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useAuthHook } from "@/hooks/useAuth";
import ProtectedRoute from "../../components/ProtectedRoute";
import { AuthProvider } from "../../context/AuthContext";
import { Property } from "../../data/models";
import Header from "../../components/client/Header";
import Sidebar from "../../components/client/Sidebar"; // Adjust path as needed
import PropertyValuationDashboard from "@/components/client/graphics/PropertyValuationDashboard";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isVerified: boolean;
  lastLoginAt: string;
  profileImageUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Define interface for stat object
interface Stat {
  title: string;
  value: string;
  change: string;
  trend: "up" | "down" | "neutral";
  icon: {
    path: string;
    bgColor: string;
  };
}

// Define interface for chart data
interface LineChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    fill: boolean;
    borderColor: string;
    tension: number;
  }[];
}

interface PieChartData {
  labels: string[];
  datasets: {
    data: number[];
    backgroundColor: string[];
  }[];
}

function useTheme() {
  const [theme, setThemeState] = useState<"light" | "dark">("light");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const storedTheme =
      (localStorage.getItem("theme") as "light" | "dark") || "light";
    setThemeState(storedTheme);
    setMounted(true);
  }, []);

  const setTheme = useCallback((newTheme: "light" | "dark") => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);
  }, []);

  useEffect(() => {
    if (mounted) {
      document.documentElement.classList.toggle("dark", theme === "dark");
    }
  }, [theme, mounted]);

  return { theme, setTheme, mounted };
}

const statusClasses = {
  active:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  pending:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
};

const roleClasses = {
  admin:
    "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  auditor: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  staff: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
  institutional_client:
    "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
};

const chartColors = [
  "rgba(79, 70, 229, 0.8)",
  "rgba(59, 130, 246, 0.8)",
  "rgba(16, 185, 129, 0.8)",
  "rgba(245, 158, 11, 0.8)",
  "rgba(239, 68, 68, 0.8)",
  "rgba(168, 85, 247, 0.8)",
];

// Helper functions
function formatRole(role: string): string {
  return role
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function ClientyDashboardContent({
  onUserSelect,
}: {
  onUserSelect?: (id: string) => void;
}) {
  const { theme, setTheme } = useTheme();
  const { currentUser, logout, token: authToken } = useAuth();
  const { user, isAuthenticated, isLoading } = useAuthHook();
  const [clientProperty, setClientProperties] = useState<Property[]>([]);
  const [isLoadingProperties, setIsLoadingProperties] = useState(true);

  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [filter, setFilter] = useState<"all" | "verified" | "unverified">(
    "all"
  );
  const [stats, setStats] = useState<Stat[]>([]);
  const [lineChartData, setLineChartData] = useState<LineChartData>({
    labels: [],
    datasets: [],
  });
  const [pieChartData, setPieChartData] = useState<PieChartData>({
    labels: [],
    datasets: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const API_BASE_URL =
    import.meta.env.VITE_API_BASE_URL ||
    "https://valuate-api.vercel.app/api/v1";

  useEffect(() => {
    if (users.length > 0) {
      const filtered = users.filter(
        (user: User) =>
          filter === "all" ||
          (filter === "verified" ? user.isVerified : !user.isVerified)
      );
      setFilteredUsers(filtered);
    }
  }, [filter, users]);

  const handleAction = (action: string) => {
    switch (action) {
      case "view-report":
        alert("Viewing report...");
        break;
      case "view-data":
        alert("Navigating to Data...");
        break;
      default:
        break;
    }
  };

  // Fetch client properties from API
  useEffect(() => {
    const fetchClientProperties = async () => {
      if (!user?.id) return;

      try {
        setIsLoadingProperties(true);
        setError(null);

        const token = localStorage.getItem("valuation_token");
        const response = await fetch(
          `${API_BASE_URL}/properties/by-upi?upi=${encodeURIComponent(
            user.upi
          )}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch properties: ${response.status}`);
        }

        const data = await response.json();
        if (data.success && data.data) {
          setClientProperties(data.data);
          setLoading(false);
        } else {
          setClientProperties([]);
        }
      } catch (err) {
        console.error("Error fetching properties:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load properties"
        );
      } finally {
        setIsLoadingProperties(false);
      }
    };

    if (isAuthenticated && user?.id) {
      fetchClientProperties();
    }
  }, [user?.id, isAuthenticated, API_BASE_URL]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 p-4 rounded-md border border-red-200 dark:border-red-800">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
              Error loading dashboard
            </h3>
            <p className="text-sm text-red-700 dark:text-red-300 mt-2">
              {error}
            </p>
            {/* <button
              onClick={fetchDashboardData}
              className="mt-3 bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 px-3 py-1 rounded text-sm hover:bg-red-200 dark:hover:bg-red-700"
            >
              Try Again
            </button> */}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-white">
      <Header theme={theme} setTheme={setTheme} logout={logout} /> 

      <div className="flex">
        <Sidebar
          onViewData={() => handleAction("view-data")}
          onViewReport={() => handleAction("view-report")}
        />

        <main className="flex-1 lg:ml-64">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-2xl font-semibold my-5 text-gray-900 dark:text-white">
              Dashboard
            </h1>
            <PropertyValuationDashboard propertyId={clientProperty.id} />
          </div>
        </main>
      </div>
    </div>
  );
}

export default function ClientDashboard() {
  return (
    <AuthProvider>
      <ProtectedRoute allowedRoles={["CLIENT"]}>
        <ClientyDashboardContent />
      </ProtectedRoute>
    </AuthProvider>
  );
}
